import { useContext, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AppContext } from "../../context/AppContext";
import { useTranslation } from "react-i18next";

import HeroHM from "../../components/heatmaps/HeroHM";
import StepBoxes from "../../components/StepBoxes";
import ButtonBtn from "../../components/buttons/ButtonBtn";
import References from "../../components/References";
import PriceList from "../../components/PriceList";
import BannerTestInvite from "../../components/BannerTestInvite";
import FAQBox from "../../components/FAQBox";
import WhatItCanDoHM from "../../components/WhatItCanDoHM";
import ResultsHM from "../../components/ResultsHM";
import ForWhomHM from "../../components/ForWhomHM";
import ImageSliderHM from "../../components/heatmaps/ImageSliderHM";
import ImageDropzone from "../../components/heatmaps/ImageDropzone";
import ModalLoading from "../../components/ModalLoading";
import SimpleAlert from "../../components/SimpleAlert";
import ModalResult from "../../components/ModalResult";
import confetti from "../../img/confetti.svg";
import dangerIcon from "../../img/icons/danger.svg";

import styles from "../../css/heatmaps/Heatmaps.module.css";
import axiosInstance, { clearUserDetailStorage } from "../../api/AxiosConfig";
import TryPermoniQSection from "../../components/TryPermoniqSection";
import ModalConsultation from "../../components/ModalConsultation";
import ModalCreditLogs from "../../components/heatmaps/ModalCreditLogs";

const REQUIRE_PASSWORD = false; // ← Změň na true pro aktivaci hesla

const PASSWORD_HASH = "a4f5c8d2";
const PASSWORD_PARTS = ["gate", "com"];
const PASSWORD_ORDER = [1, 0];

// Funkce pro sestavení hesla z částí
const getCorrectPassword = () => {
  return PASSWORD_ORDER.map((i) => PASSWORD_PARTS[i]).join("");
};

// Jednoduchý hash pro ověření
const hashPassword = (input) => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).substring(0, 8);
};

const HeatmapsLP = () => {
  const { t } = useTranslation();
  const { lng } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    setIsUserDataLoading,
    isLogged,
    userData,
    setUserData,
    checkLoginStatus,
    setShowModalCredit,
    setModalPriceListSelectedPlan,
    showCreditLogsModal,
  } = useContext(AppContext);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [minimumLoadingTime, setMinimumLoadingTime] = useState(true);

  // Password protection state
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(!REQUIRE_PASSWORD);
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);

  // Alert state
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ text: "", type: "base" });

  // Unified loading state
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Načítání...");

  // Payment result modal state
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success' | 'canceled' | 'pending' | null
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Copy for payment statuses (Czech as per requirement)
  const paymentCopy = useMemo(
    () => ({
      success: {
        title: t("heatmaps.payment.success.title", "Zaplaceno"),
        subtitle: t(
          "heatmaps.payment.success.subtitle",
          "Děkujeme! Vaše platba byla úspěšně dokončena."
        ),
        description: t(
          "heatmaps.payment.success.description",
          "Potvrzení a další informace jsme vám odeslali e-mailem. V případě dotazů nás neváhejte kontaktovat."
        ),
        buttonText: t("heatmaps.payment.success.buttonText", "Rozumím"),
      },
      canceled: {
        title: t("heatmaps.payment.canceled.title", "Zrušeno"),
        subtitle: t(
          "heatmaps.payment.canceled.subtitle",
          "Platba byla zrušena."
        ),
        description: t(
          "heatmaps.payment.canceled.description",
          "Transakce nebyla dokončena. Pokud si přejete pokračovat, můžete platbu kdykoli zopakovat."
        ),
        buttonText: t(
          "heatmaps.payment.canceled.buttonText",
          "Opakovat platbu"
        ),
      },
      pending: {
        title: t(
          "heatmaps.payment.pending.title",
          "Nevyřízeno, neúspěšný pokus"
        ),
        subtitle: t(
          "heatmaps.payment.pending.subtitle",
          "Platba nebyla dokončena."
        ),
        description: t(
          "heatmaps.payment.pending.description",
          "Zdá se, že došlo k přerušení nebo chybě během platby. Doporučujeme zkontrolovat stav platby ve vašem bankovnictví nebo to zkusit znovu."
        ),
        buttonText: t("heatmaps.payment.pending.buttonText", "Opakovat platbu"),
      },
    }),
    []
  );

  const heatmapsSteps = t("heatmaps.procedure.steps", { returnObjects: true });
  const faqData = t("heatmaps.faq", { returnObjects: true });

  // On mount, ensure login status is checked (important when coming from hub)
  useEffect(() => {
    checkLoginStatus();
  }, []);

  // Mark origin so navbar logo on login can return user correctly
  useEffect(() => {
    try {
      sessionStorage.setItem("last_lp", "heatmaps");
    } catch (e) {
      // ignore
    }
  }, []);

  // Detect payment status from URL for logged-in users and show modal
  useEffect(() => {
    if (isLoading) return; // wait until initial loading resolves to avoid flicker
    if (!isLogged) return; // show only for logged-in users

    const params = new URLSearchParams(location.search);
    const rawStatus = params.get("p_status");
    // Normalize possible values (support both 'cancel' and 'canceled')
    const status = rawStatus === "cancel" ? "canceled" : rawStatus;
    if (status && ["success", "canceled", "pending"].includes(status)) {
      setPaymentStatus(status);
      setShowPaymentModal(true);
    }
  }, [isLoading, isLogged, location.search]);

  // When the payment modal actually opens with a known status, clear cached user data and refetch in background
  useEffect(() => {
    if (!showPaymentModal) return;
    if (
      !paymentStatus ||
      !["success", "canceled", "pending"].includes(paymentStatus)
    )
      return;

    (async () => {
      try {
        await clearUserDetailStorage();
      } catch (e) {
        // ignore
      }
      try {
        Cookies.remove("userDataTimestamp");
      } catch (e) {
        // ignore
      }
      if (typeof setIsUserDataLoading === "function") {
        setIsUserDataLoading(true);
      }
      // Trigger background refetch of fresh user data via API
      try {
        const { data } = await axiosInstance.get("user/detail/");
        if (typeof setUserData === "function") {
          setUserData(data);
        }
      } catch (err) {
        // optional: fallback to login status check
        try {
          checkLoginStatus();
        } catch (_) {}
      } finally {
        if (typeof setIsUserDataLoading === "function") {
          setIsUserDataLoading(false);
        }
      }
    })();
  }, [showPaymentModal, paymentStatus]);

  const clearPaymentStatusFromUrl = () => {
    const params = new URLSearchParams(location.search);
    if (!params.has("p_status")) return;
    params.delete("p_status");
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true }
    );
  };

  const repeatPayment = () => {
    setShowPaymentModal(false);
    clearPaymentStatusFromUrl();
    // Otevřít modal s ceníkem/platbou
    if (typeof setShowModalCredit === "function") {
      setShowModalCredit(true);
    }
  };

  // Effect pro správné řízení loading stavů
  useEffect(() => {
    // Pokud password není vyžadován nebo je už autentizován, pokračuj s loading
    if (!REQUIRE_PASSWORD || isAuthenticated) {
      let minTimeTimer;

      // Minimální čas loading (2 sekundy)
      const startTime = Date.now();

      const checkLoadingComplete = () => {
        const elapsed = Date.now() - startTime;
        const dataReady =
          isLogged !== null &&
          (isLogged === false || (isLogged === true && userData));

        if (dataReady && elapsed >= 2000) {
          setIsLoading(false);
        } else if (dataReady) {
          // Data jsou připravena, ale ještě neuplynuly 2 sekundy
          const remainingTime = 2000 - elapsed;
          minTimeTimer = setTimeout(() => {
            setIsLoading(false);
          }, remainingTime);
        }
      };

      checkLoadingComplete();

      // Kontroluj znovu při změně dat
      const interval = setInterval(checkLoadingComplete, 100);

      return () => {
        clearTimeout(minTimeTimer);
        clearInterval(interval);
      };
    }
  }, [isLogged, userData, isAuthenticated]);

  const notLoggedClick = () => {
    navigate(`/${lng}/login/`, {
      state: {
        from: location.pathname,
      },
    });
  };

  const showAlert = (message, type = "base") => {
    setAlertMessage({ text: message, type });
    setIsAlertVisible(true);
  };

  const closeAlert = () => {
    setIsAlertVisible(false);
  };

  // Funkce pro ověření hesla
  const handlePasswordSubmit = () => {
    const correctPassword = getCorrectPassword();
    const inputHash = hashPassword(password);

    if (password === correctPassword || inputHash === PASSWORD_HASH) {
      // Okamžitě zobraz loading
      setIsPasswordSubmitting(true);
      setLoadingMessage("Ověřuji heslo...");
      setIsLoading(true);

      // Po ověření hesla pokračuj s normálním načítáním
      setTimeout(() => {
        setIsAuthenticated(true);
        setIsPasswordSubmitting(false);
        setLoadingMessage("Načítání...");
        // Neresetuj isLoading - nech to hlavní useEffect
      }, 2000);
    } else {
      showAlert("Špatné heslo!", "danger");
      setPassword(""); // Vymaž nesprávné heslo
    }
  };

  // Pokud se zpracovává heslo nebo je vyžadováno heslo a není zadané, zobraz příslušný formulář/loading
  if (REQUIRE_PASSWORD && !isAuthenticated) {
    // Pokud se zpracovává heslo, zobraz loading
    if (isPasswordSubmitting) {
      return (
        <>
          <ModalLoading message={loadingMessage} />
        </>
      );
    }

    // Jinak zobraz password formulář
    return (
      <>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <h2>Zadejte heslo pro přístup</h2>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handlePasswordSubmit()}
            style={{ padding: "8px", fontSize: "1rem", width: "200px" }}
            placeholder="Heslo..."
          />
          <button
            onClick={handlePasswordSubmit}
            style={{ padding: "8px 16px", fontSize: "1rem" }}
          >
            Vstoupit
          </button>
        </div>
        {isAlertVisible && (
          <SimpleAlert
            message={alertMessage.text}
            onClose={closeAlert}
            type={alertMessage.type}
          />
        )}
      </>
    );
  }

  // Jednotný loading stav
  if (isLoading || isLogged === null) {
    return (
      <>
        <ModalLoading message={loadingMessage} />
      </>
    );
  }

  return isLogged ? (
    <>
      <div className={`body body--app ${styles.body}`}>
        {/* Vždy zobrazíme dropzone; odeslání je řízené kredity */}
        <ImageDropzone
          availableCredits={Math.max(0, userData?.available_credits ?? 0)}
          onBlockedSubmit={(msg) => showAlert(msg, "danger")}
        />
        {(userData?.available_credits ?? 0) < 1 && (
          <PriceList
            notLoggedClick={notLoggedClick}
            onChoosePlan={(plan) => {
              // Preselect and open modal directly on billing form
              setModalPriceListSelectedPlan?.(plan);
              setShowModalCredit?.(true);
            }}
          />
        )}
        {showPaymentModal && paymentStatus && (
          <ModalResult
            onClose={() => {
              setShowPaymentModal(false);
              clearPaymentStatusFromUrl();
            }}
            iconSrc={
              paymentStatus === "success"
                ? confetti
                : paymentStatus === "canceled" || paymentStatus === "pending"
                ? dangerIcon
                : null
            }
            iconScale={
              paymentStatus === "canceled" || paymentStatus === "pending"
                ? 2
                : 1
            }
            title={paymentCopy[paymentStatus].title}
            subtitle={paymentCopy[paymentStatus].subtitle}
            onButtonClick={
              paymentStatus === "canceled" || paymentStatus === "pending"
                ? repeatPayment
                : undefined
            }
            description={paymentCopy[paymentStatus].description}
            buttonText={paymentCopy[paymentStatus].buttonText}
            showCloseButton={true}
          />
        )}
        {/* {showCreditLogsModal && (
          <ModalCreditLogs />
        )} */}
        {isAlertVisible && (
          <SimpleAlert
            message={alertMessage.text}
            onClose={closeAlert}
            type={alertMessage.type}
          />
        )}
      </div>
    </>
  ) : (
    <>
      <div className={`body ${styles.toolsBody}`}>
        <HeroHM notLoggedClick={notLoggedClick} />
        <ImageSliderHM />
        <WhatItCanDoHM notLoggedClick={notLoggedClick} />
        <section
          className={`section section--q-htmp ${styles.section}`}
          data-component="how-to-set-up"
          id="how-to-set-up"
        >
          <article className="container">
            <h2 className="title">{t("heatmaps.procedure.heading")}</h2>
            <div className="how" id="how" style={{ marginTop: "20px" }}>
              {heatmapsSteps.map((item, index) => (
                <StepBoxes key={index} text={item} index={index} />
              ))}
            </div>
            <ButtonBtn
              onClick={(e) => {
                e.target.blur();
                notLoggedClick();
              }}
              style={{ marginTop: "35px", marginInline: "auto" }}
              hoverBg={"white"}
              hoverColor={"black"}
            >
              {t("heatmaps.procedure.btn")}
            </ButtonBtn>
          </article>
        </section>
        <ResultsHM />
        {/* <References
          heading={t("heatmaps.references.heading")}
          text={t("heatmaps.references.text")}
          maxWidth={"828px"}
          notLoggedClick={notLoggedClick}
        /> */}
        {/* <BannerTestInvite notLoggedClick={notLoggedClick} /> */}
        <PriceList
          notLoggedClick={notLoggedClick}
          onChoosePlan={(plan) => {
            setModalPriceListSelectedPlan?.(plan);
            setShowModalCredit?.(true);
          }}
        />
        <FAQBox faq={faqData} />
        <ForWhomHM />
        <TryPermoniQSection />
        {/* Mount the consultation modal so openModal() from TryPermoniqSection can display it */}
        <ModalConsultation />
        {isAlertVisible && (
          <SimpleAlert
            message={alertMessage.text}
            onClose={closeAlert}
            type={alertMessage.type}
          />
        )}
      </div>
    </>
  );
};

export default HeatmapsLP;
